import { FC, useState } from 'react';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { graphql, useRelayEnvironment } from 'react-relay';

import TypeaheadSelect from 'dibs-typeahead-select/exports/TypeaheadSelect';
import { SelectOption, SingleOnChangeArgs } from 'dibs-elements/exports/SingleSelect';
import {
    VerticalType,
    verticalSpecificsMap,
    SEARCH_CREATOR,
    SEARCH_ARTIST,
    SEARCH_DESIGNER,
} from '../SellerApplicationFormReducer';

import {
    CreatorsSearchQuery,
    CreatorsSearchQuery$data,
} from './__generated__/CreatorsSearchQuery.graphql';

const messages = defineMessages({
    searchForCreatorGeneric: {
        id: 'SellerApplicationForm.CreatorsSearch.searchForCreatorPlaceholder',
        defaultMessage: 'Search for Creator/Artist/Designer',
    },
    [SEARCH_CREATOR]: {
        id: 'SellerApplicationForm.searchCreator',
        defaultMessage: 'Search for Creator',
    },
    [SEARCH_ARTIST]: {
        id: 'SellerApplicationForm.searchArtist',
        defaultMessage: 'Search for Artist',
    },
    [SEARCH_DESIGNER]: {
        id: 'SellerApplicationForm.searchDesigner',
        defaultMessage: 'Search for Designer',
    },
});

type Props = {
    onChange: (creator: SingleOnChangeArgs) => void;
    vertical?: VerticalType | null;
    value?: SingleOnChangeArgs;
};

const CreatorsSearch: FC<Props> = ({ vertical, onChange, value }) => {
    const environment = useRelayEnvironment();
    const intl = useIntl();
    const [creator, setCreator] = useState<SingleOnChangeArgs>(null);
    const placeholderCopy = vertical
        ? intl.formatMessage(messages[verticalSpecificsMap[vertical].creatorArtistDesignerKey])
        : intl.formatMessage(messages.searchForCreatorGeneric);

    let options: SelectOption[];
    if (value) {
        options = [value];
    } else if (creator) {
        options = [creator];
    } else {
        options = [];
    }

    const getOptions = (payload: CreatorsSearchQuery$data): SelectOption[] => {
        return (payload.viewer.creators?.edges || []).reduce<SelectOption[]>((list, edge) => {
            const { displayName, serviceId, statuses } = edge?.node || {};
            const statusVerticalKey = vertical?.toUpperCase();
            if (
                serviceId &&
                statusVerticalKey &&
                statuses?.[statusVerticalKey as keyof typeof statuses]
            ) {
                list.push({
                    label: displayName || '',
                    value: serviceId || '',
                });
            } else if (serviceId && !vertical) {
                list.push({
                    label: displayName || '',
                    value: serviceId || '',
                });
            }
            return list;
        }, []);
    };

    return (
        <TypeaheadSelect<CreatorsSearchQuery>
            dataTn="item-creator-search-select"
            name="item-creator-search-select"
            onChange={option => {
                setCreator(option);
                onChange(option);
            }}
            value={creator?.value || value?.value || ''}
            options={options}
            placeholder={placeholderCopy}
            relayEnvironment={environment}
            getOptions={getOptions}
            minSearchLength={3}
            query={graphql`
                query CreatorsSearchQuery($value: String = "") {
                    viewer {
                        creators(contains: $value, first: 50, creatorStatus: [approved]) {
                            edges {
                                node {
                                    serviceId
                                    displayName
                                    statuses {
                                        FURNITURE
                                        JEWELRY
                                        FASHION
                                        ART
                                    }
                                }
                            }
                        }
                    }
                }
            `}
        />
    );
};

export default CreatorsSearch;
